import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import ProductCard from '../components/ProductCard';
import { PROMO_BANNERS, FEATURED_BANNER, STORE_INFO } from '../mock';
import { fetchProducts, fetchCategories, fetchBrands, subscribeNewsletter } from '../lib/api';
import { Star, Truck, ShieldCheck, Award, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [p, c, b] = await Promise.all([
          fetchProducts({ limit: 8 }),
          fetchCategories(),
          fetchBrands(),
        ]);
        setProducts(p);
        setCategories(c.filter(x => ['protein', 'fat-burners', 'muscle-builders', 'recovery', 'health-balance'].includes(x.id)));
        setBrands(b);
      } catch (e) {
        console.error(e);
        toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, [toast]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await subscribeNewsletter(email);
      toast({ title: 'Subscribed!', description: "You're on the list for weekly offers." });
      setEmail('');
    } catch {
      toast({ title: 'Error', description: 'Could not subscribe. Try again.', variant: 'destructive' });
    } finally { setSubscribing(false); }
  };

  return (
    <div>
      <HeroSlider />

      <div className="bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div className="flex items-center gap-3"><Truck className="w-6 h-6 text-[#bfff00]" /><div><div className="font-bold">Free Shipping</div><div className="text-xs text-neutral-400">On orders over ${STORE_INFO.freeShipping}</div></div></div>
          <div className="flex items-center gap-3"><ShieldCheck className="w-6 h-6 text-[#bfff00]" /><div><div className="font-bold">Price Match</div><div className="text-xs text-neutral-400">Guaranteed lowest prices</div></div></div>
          <div className="flex items-center gap-3"><Award className="w-6 h-6 text-[#bfff00]" /><div><div className="font-bold">Authentic Brands</div><div className="text-xs text-neutral-400">100% genuine products</div></div></div>
          <div className="flex items-center gap-3"><Star className="w-6 h-6 text-[#bfff00]" /><div><div className="font-bold">Expert Advice</div><div className="text-xs text-neutral-400">In-store coaches</div></div></div>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PROMO_BANNERS.map(b => (
          <Link key={b.id} to="/shop" className="block rounded-lg overflow-hidden hover:scale-[1.02] transition-transform shadow-md">
            <img src={b.image} alt={b.alt} className="w-full h-auto" />
          </Link>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4">
        <Link to="/shop" className="block rounded-lg overflow-hidden hover:scale-[1.01] transition-transform shadow-md">
          <img src={FEATURED_BANNER.image} alt={FEATURED_BANNER.alt} className="w-full h-auto" />
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">Popular Categories</h2>
            <p className="text-neutral-500 mt-1">Shop by what fuels your goals.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {categories.map(cat => (
            <Link key={cat.id} to={`/shop?category=${cat.id}`} className="group block text-center">
              <div className="aspect-square bg-neutral-100 rounded-full p-6 mb-3 overflow-hidden group-hover:bg-[#bfff00]/20 transition-colors">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" />
              </div>
              <h3 className="font-bold text-sm md:text-base uppercase tracking-wide group-hover:text-[#0a0a0a]">{cat.name}</h3>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">Recommended</h2>
          <Link to="/shop" className="text-sm font-semibold flex items-center gap-1 hover:underline">View All <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-neutral-400" /></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      <section className="bg-neutral-50 py-14 mt-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight">Discover Products You'll Love</h2>
              <p className="text-neutral-500 mt-1">Shop by your favourite brand.</p>
            </div>
            <Link to="/brands" className="text-sm font-semibold flex items-center gap-1 hover:underline">View All <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-4">
            {brands.slice(0, 14).map(b => (
              <Link key={b.id} to={`/shop?brand=${b.id}`} className="bg-white rounded-lg p-4 flex items-center justify-center hover:shadow-lg hover:-translate-y-1 transition-all aspect-square">
                <img src={b.logo} alt={b.name} className="max-w-full max-h-full object-contain" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">Sign Up For Our Newsletter</h2>
          <p className="text-neutral-400 mt-2">Stay up to date with recent news, advice and weekly offers.</p>
          <form onSubmit={handleSubscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" className="flex-1 px-4 py-3 rounded bg-neutral-900 border border-neutral-700 text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#bfff00]" />
            <Button type="submit" disabled={subscribing} className="bg-[#bfff00] hover:bg-[#a8e600] text-black font-bold px-8">{subscribing ? 'Subscribing...' : 'Subscribe'}</Button>
          </form>
          <p className="text-xs text-neutral-500 mt-3">We respect your privacy. Unsubscribe at any time.</p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
